import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tabs,
  Button,
  Card,
  Tag,
  message,
  Spin,
  Empty,
  Avatar,
  Popconfirm,
  Row,
  Col,
  Space,
} from "antd";
import {
  UserOutlined,
  MailOutlined,
  BankOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { API_CONST } from "../../../const";

const PageHeader = styled.div`
  margin-bottom: 24px;
  h1 {
    font-size: 24px;
    font-weight: 600;
    color: #014c75;
    margin: 0 0 8px 0;
  }
  p {
    color: #666;
    margin: 0;
    font-size: 14px;
  }
`;

const MyClientsWrapper = styled.div``;

const ClientCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  transition: box-shadow 0.2s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  .client-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
  }
  .client-name {
    font-size: 16px;
    font-weight: 600;
    color: #014c75;
    margin: 0;
  }
  .client-org {
    font-size: 13px;
    color: #666;
    margin: 2px 0 0;
  }
  .client-meta {
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #555;
  }
  .client-meta-item {
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .client-stats {
    display: flex;
    gap: 0;
    border-top: 1px solid #f0f0f0;
    padding-top: 12px;
    margin-top: 4px;
  }
  .stat-item {
    flex: 1;
    text-align: center;
    border-right: 1px solid #f0f0f0;
    &:last-child {
      border-right: none;
    }
  }
  .stat-value {
    font-size: 18px;
    font-weight: 700;
    color: #014c75;
  }
  .stat-label {
    font-size: 11px;
    color: #999;
    margin-top: 2px;
  }
  .client-actions {
    margin-top: 12px;
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }
`;

const MyClients = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statsMap, setStatsMap] = useState({});
  const [activeTab, setActiveTab] = useState("active");
  const [processingId, setProcessingId] = useState(null);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(API_CONST.PARTNER_RELATION_MY_CLIENTS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClients(response.data.relations || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      message.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStatsForClient = useCallback(async (relationId) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${API_CONST.PARTNER_RELATION_CLIENT_STATS}/${relationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatsMap((prev) => ({
        ...prev,
        [relationId]: response.data.stats || {},
      }));
    } catch (error) {
      console.error("Error fetching client stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  useEffect(() => {
    const activeClients = clients.filter(
      (c) => c.status === "active" && c.customerOrganization
    );
    activeClients.forEach((c) => {
      if (!statsMap[c.id]) {
        fetchStatsForClient(c.id);
      }
    });
  }, [clients, fetchStatsForClient, statsMap]);

  const handleAccept = async (client) => {
    setProcessingId(client.id);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.BASE_URL}/partner-relation/accept-id/${client.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Invitation accepted successfully");
      fetchClients();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to accept invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (client) => {
    setProcessingId(client.id);
    try {
      const token = localStorage.getItem("authToken");
      await axios.post(
        `${API_CONST.PARTNER_RELATION_DECLINE}/${client.id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success("Invitation declined");
      fetchClients();
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to decline invitation");
    } finally {
      setProcessingId(null);
    }
  };

  const activeClients = clients.filter((c) => c.status === "active");
  const pendingClients = clients.filter((c) => c.status === "pending_invite");

  const getStatusBadge = (client) => {
    if (client.status === "active") {
      return <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>;
    }
    if (client.status === "pending_invite") {
      return <Tag color="orange" icon={<ClockCircleOutlined />}>Pending</Tag>;
    }
    return <Tag>{client.status}</Tag>;
  };

  const renderActiveClientCard = (client) => {
    const stats = statsMap[client.id] || {};
    return (
      <Col xs={24} sm={12} lg={8} xl={6} key={client.id}>
        <ClientCard
          style={{ cursor: "pointer" }}
          onClick={() => navigate(`/partner/my-clients/${client.id}`)}
        >
          <div className="client-header">
            <Avatar
              size={44}
              icon={<BankOutlined />}
              style={{ background: "#e6f7ff", color: "#014c75" }}
            />
            <div>
              <p className="client-name">
                {client.customerOrganization || "Client"}
              </p>
              {client.invitedName && (
                <p className="client-org">{client.invitedName}</p>
              )}
            </div>
            <div style={{ marginLeft: "auto" }}>{getStatusBadge(client)}</div>
          </div>

          <div className="client-meta">
            {client.invitedEmail && (
              <span className="client-meta-item">
                <MailOutlined style={{ color: "#999" }} />
                {client.invitedEmail}
              </span>
            )}
            {client.acceptedAt && (
              <span className="client-meta-item">
                <CheckCircleOutlined style={{ color: "#999" }} />
                Since{" "}
                {new Date(client.acceptedAt).toLocaleDateString("en-US", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            )}
          </div>

          {client.customerOrganization && (
            <div className="client-stats">
              <div className="stat-item">
                <div className="stat-value">{stats.jobsPosted ?? "-"}</div>
                <div className="stat-label">Jobs Posted</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{stats.applicationsSubmitted ?? "-"}</div>
                <div className="stat-label">Submitted</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{stats.hiredByClient ?? "-"}</div>
                <div className="stat-label">Hired</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{stats.activeHiresByClient ?? "-"}</div>
                <div className="stat-label">Active</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{stats.applicationsRejected ?? "-"}</div>
                <div className="stat-label">Rejected</div>
              </div>
            </div>
          )}
        </ClientCard>
      </Col>
    );
  };

  const renderPendingClientCard = (client) => (
    <Col xs={24} sm={12} lg={8} xl={6} key={client.id}>
      <ClientCard style={{ borderLeft: "3px solid #faad14" }}>
        <div className="client-header">
          <Avatar
            size={44}
            icon={<BankOutlined />}
            style={{ background: "#fff7e6", color: "#faad14" }}
          />
          <div>
            <p className="client-name">
              {client.customerOrganization || "Unknown Client"}
            </p>
            {client.invitedEmail && (
              <p className="client-org">{client.invitedEmail}</p>
            )}
          </div>
          <div style={{ marginLeft: "auto" }}>{getStatusBadge(client)}</div>
        </div>

        <div className="client-meta">
          {client.invitedAt && (
            <span className="client-meta-item">
              <MailOutlined style={{ color: "#999" }} />
              Invited{" "}
              {new Date(client.invitedAt).toLocaleDateString("en-US", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </span>
          )}
        </div>

        <div className="client-actions">
          <Popconfirm
            title="Accept this invitation?"
            description="You will be added as a partner for this client."
            onConfirm={() => handleAccept(client)}
            okText="Accept"
            okButtonProps={{ style: { background: "#00d9a9", borderColor: "#00d9a9" } }}
          >
            <Button
              size="small"
              type="primary"
              style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
              loading={processingId === client.id}
            >
              Accept
            </Button>
          </Popconfirm>
          <Popconfirm
            title="Decline this invitation?"
            description="This invitation will be declined and cannot be undone."
            onConfirm={() => handleDecline(client)}
            okText="Decline"
            okButtonProps={{ danger: true }}
          >
            <Button
              size="small"
              danger
              loading={processingId === client.id}
            >
              Decline
            </Button>
          </Popconfirm>
        </div>
      </ClientCard>
    </Col>
  );

  const tabItems = [
    {
      key: "active",
      label: (
        <span>
          <CheckCircleOutlined /> Active Clients ({activeClients.length})
        </span>
      ),
      children: (
        <Spin spinning={loading}>
          {activeClients.length === 0 && !loading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No active clients yet"
              style={{ padding: "60px 0" }}
            />
          ) : (
            <Row gutter={[20, 20]} style={{ padding: "16px 4px 4px" }}>
              {activeClients.map(renderActiveClientCard)}
            </Row>
          )}
        </Spin>
      ),
    },
    {
      key: "pending",
      label: (
        <span>
          <ClockCircleOutlined /> Pending Invitations ({pendingClients.length})
        </span>
      ),
      children: (
        <Spin spinning={loading}>
          {pendingClients.length === 0 && !loading ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No pending invitations"
              style={{ padding: "60px 0" }}
            />
          ) : (
            <Row gutter={[20, 20]} style={{ padding: "16px 4px 4px" }}>
              {pendingClients.map(renderPendingClientCard)}
            </Row>
          )}
        </Spin>
      ),
    },
  ];

  return (
    <MyClientsWrapper>
      <PageHeader>
        <h1>My Clients</h1>
        <p>View your active client relationships and respond to pending invitations</p>
      </PageHeader>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          marginBottom: 20,
        }}
      >
        <Space.Compact
          style={{
            background: "#f8f9fd",
            padding: "8px 16px",
            borderRadius: 8,
            border: "1px solid #e8e8e8",
          }}
        >
          <span style={{ color: "#666", fontSize: 13, marginRight: 16 }}>
            <TeamOutlined style={{ marginRight: 6 }} />
            Total: <strong>{clients.length}</strong>
          </span>
          <span style={{ color: "#52c41a", fontSize: 13, marginRight: 16 }}>
            <CheckCircleOutlined style={{ marginRight: 6 }} />
            Active: <strong>{activeClients.length}</strong>
          </span>
          <span style={{ color: "#faad14", fontSize: 13 }}>
            <ClockCircleOutlined style={{ marginRight: 6 }} />
            Pending: <strong>{pendingClients.length}</strong>
          </span>
        </Space.Compact>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        style={{ background: "#fff" }}
        tabBarStyle={{ padding: "4px 8px 0" }}
      />
    </MyClientsWrapper>
  );
};

export default MyClients;
